import React, { PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Extra, InventoryEntry } from "../lib/model";
import { useIntl } from "react-intl";
import { getLanguageFromFullLocale } from "../lib/config";
import { useGrpcClient, usePropertyGrpcCall } from "../lib/grpc";
import { ConditionAPIPromiseClient } from "../gen/witbooking/backoffice/condition/v1/condition_api_grpc_web_pb";
import { GetConditionsRequest } from "../gen/witbooking/backoffice/condition/v1/condition_api_pb";
import { InventoryAPIPromiseClient } from "../gen/witbooking/backoffice/inventory/v1/inventory_api_grpc_web_pb";
import { GetInventoryLinesRequest } from "../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import { ExtraAPIPromiseClient } from "../gen/witbooking/backoffice/extra/v1/extra_api_grpc_web_pb";
import { GetExtrasRequest } from "../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import { PromotionAPIPromiseClient } from "../gen/witbooking/backoffice/promotion/v1/promotion_api_grpc_web_pb";
import { GetPromotionsRequest } from "../gen/witbooking/backoffice/promotion/v1/promotion_api_pb";

interface PropertyAdminDataProviderProps {
  /**
   * If specified, a loader will be displayed while loading, otherwise, the child component
   * will be rendered directly.
   */
  loader?: ReactNode;

  loadInventories?: boolean;
  loadExtraLegacyMapping?: boolean;
  loadPromoLegacyMapping?: boolean;
  loadConditionLegacyMapping?: boolean;
}

export interface PropertyData {
  inventories?: InventoryEntry[];
  inventoryLegacyMapping?: { [key: string]: string };
  blockingInventoryTickers?: string[];
  promoLegacyMapping?: { [key: string]: string };
  extraLegacyMapping?: { [key: string]: string };
  extras?: Extra[];
  conditionLegacyMapping?: { [key: string]: string };
  conditionsInUse?: string[];
  reload: () => void;
}

/**
 * Undefined if it's still loading
 * null if an error occurred while loading.
 */
const propertyDataContext = React.createContext<PropertyData | undefined | null>({
  reload: () => {},
});

/**
 * Can be used to load property data if the data is needed for a form to render options (i.e. related data)
 *
 * @param children
 * @param loader
 * @param loadInventories
 * @param loadPromoLegacyMapping
 * @param loadExtraLegacyMapping
 * @param loadConditionLegacyMapping
 * @constructor
 */
export function PropertyAdminDataProvider({ children, loader, loadInventories, loadPromoLegacyMapping, loadExtraLegacyMapping, loadConditionLegacyMapping }: PropsWithChildren<PropertyAdminDataProviderProps>) {
  const [data, setData] = useState<PropertyData | undefined | null>(undefined);
  const locale = useIntl().locale;
  const conditionsClient = useGrpcClient(ConditionAPIPromiseClient);
  const inventoryClient = useGrpcClient(InventoryAPIPromiseClient);
  const promoClient = useGrpcClient(PromotionAPIPromiseClient);
  const extraClient = useGrpcClient(ExtraAPIPromiseClient);
  const getConditions = usePropertyGrpcCall(conditionsClient, conditionsClient.getConditions);
  const getInventoryLines = usePropertyGrpcCall(inventoryClient, inventoryClient.getInventoryLines);
  const getExtras = usePropertyGrpcCall(extraClient, extraClient.getExtras);
  const getPromotions = usePropertyGrpcCall(promoClient, promoClient.getPromotions);

  const load = useCallback(async () => {
    try {
      const inventoryRequest = loadInventories ? new GetInventoryLinesRequest() : undefined;
      if (inventoryRequest) {
        inventoryRequest.setLocale(getLanguageFromFullLocale(locale));
        inventoryRequest.setOnlyValidLines(true);
      }
      const inventoriesPromise = inventoryRequest ? getInventoryLines(inventoryRequest) : undefined;
      const promoMappingsPromise = loadPromoLegacyMapping ? getPromotions(new GetPromotionsRequest()) : undefined;
      const extraMappingsPromise = loadExtraLegacyMapping ? getExtras(new GetExtrasRequest()) : undefined;
      const conditionMappingsPromise = loadConditionLegacyMapping ? getConditions(new GetConditionsRequest()) : undefined;
      const [inventories, promoMappings, extraMappings, conditionMappings] = await Promise.all([inventoriesPromise, promoMappingsPromise, extraMappingsPromise, conditionMappingsPromise]);
      setData({
        inventories: inventories?.getInventoryLinesList().map(v => ({
          accommodation: v.getAccommodation()!.toObject(),
          condition: v.getCondition()!.toObject(),
          configuration: v.getConfiguration()!.toObject(),
          mealPlan: v.getMealPlan()!.toObject(),
          ticker: v.getCode(),
          legacyId: v.getLegacyId(),
        })),
        inventoryLegacyMapping: inventories?.getInventoryLinesList().reduce<{ [key: string]: string }>((prev, next) => {
          prev[next.getCode()] = next.getLegacyId().toString();
          return prev;
        }, {}),
        blockingInventoryTickers: inventories?.getInventoryLinesList().filter(v => v.getBlockingType() === "OWN").map(v => v.getCode()),
        promoLegacyMapping: promoMappings?.getPromotionsList().reduce<{ [key: string]: string }>((prev, next) => {
          prev[next.getTicker()] = next.getLegacyId().toString();
          return prev;
        }, {}),
        extraLegacyMapping: extraMappings?.getExtrasList().reduce<{ [key: string]: string }>((prev, next) => {
          prev[next.getTicker()] = next.getLegacyId().toString();
          return prev;
        }, {}),
        extras: extraMappings?.getExtrasList().map(v => ({ name: v.getName(), ticker: v.getTicker() })),
        conditionLegacyMapping: conditionMappings?.getConditionsList().reduce<{ [key: string]: string }>((prev, next) => {
          prev[next.getTicker()] = next.getLegacyId().toString();
          return prev;
        }, {}),
        conditionsInUse: conditionMappings?.getConditionsList().filter(v => v.getInUse()).map(v => v.getTicker()),
        reload: load,
      });
    } catch (e) {
      console.log("Error loading property data", e);
      setData(null);
    }
  }, [getInventoryLines, locale, getPromotions, getConditions, getExtras, loadInventories, loadPromoLegacyMapping, loadExtraLegacyMapping, loadConditionLegacyMapping]);

  useEffect(() => {
    load();
  }, [load]);

  //Normally, we'd put a loader on these scenarios when we're not loading. However, blocking the whole
  //ui for this datum doesn't seem a good idea. We let the consumer decide.
  return (
    <>
      {data === undefined && loader}
      {(data !== undefined || !loader) &&
      <propertyDataContext.Provider value={data}>
        {children}
      </propertyDataContext.Provider>
      }
    </>
  );
}

export function usePropertyData(): PropertyData | undefined | null {
  return useContext(propertyDataContext);
}

//This one is for compatibility with witadmin
export function withPropertyData<P = {}>(Component: React.ComponentType<P & { wbkData?: PropertyData | null }>): React.ComponentType<P> {
  return (props: P) => {
    const data = usePropertyData();
    return (<Component wbkData={data} {...props} />);
  };
}